import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, ErrorState, Feld, KpiCard, KpiReihe,
  LoadingState,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Der Belegungsplan — wo im Deckenhohlraum noch Platz ist.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SIE KEHRT DIE RICHTUNG UM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *     Kollisionsbericht    „Du hast falsch gezeichnet"    NACH der Arbeit
 *     Belegungsplan        „Hier ist noch Platz"          VOR der Arbeit
 *
 * Ein Werkzeug, das nur hinterher meckert, wird geduldet. Eines, das vorher
 * hilft, wird benutzt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM EIN GEZEICHNETES RASTER UND KEINE TABELLE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 4.857 Rasterspalten allein im Geschoss G01. Als Tabelle sind das 4.857
 * Zeilen, durch die niemand blättert — und die Frage „wo ist noch Platz"
 * beantwortet sich im Grundriss und nirgends sonst.
 *
 * Gezeichnet wird auf ein Canvas und nicht als 4.857 Rechtecke im DOM: Das
 * wäre dieselbe Zahl an Elementen, nur langsamer.
 */
export function Belegung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [geschoss, setGeschoss] = useState('G01');
  const [gewerk, setGewerk] = useState<string>('rlt');
  const [d, setD] = useState<any | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [unterCursor, setUnterCursor] = useState<any | null>(null);

  // ═══════════════════════════════════════════════════════════════════════
  // DER KORRIDOR WIRD DORT ABGESTECKT, WO MAN DEN PLATZ SIEHT
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Ein Formular auf einer anderen Seite, in das man Zellennummern tippt,
  // benutzt niemand. Die Reservierung entsteht auf derselben Karte, die die
  // freie Höhe zeigt — sonst ist die Antwort „hier ist Platz" wieder nur eine
  // Auskunft und kein Werkzeug.
  const [absteckmodus, setAbsteckmodus] = useState(false);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [zieht, setZieht] = useState<{ x: number; y: number } | null>(null);
  const [zieherZiel, setZieherZiel] = useState<{ x: number; y: number } | null>(null);

  const [zweck, setZweck] = useState('');
  const [planer, setPlaner] = useState('');
  const [vonMm, setVonMm] = useState<string>('');
  const [bisMm, setBisMm] = useState<string>('');
  const [frist, setFrist] = useState('14');
  const [besetzt, setBesetzt] = useState<any[] | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [schickt, setSchickt] = useState(false);

  const leinwand = useRef<HTMLCanvasElement | null>(null);

  const laden = () => {
    if (!projekt) return;

    setLaeuft(true);
    setFehler(null);

    api.koordinationBelegung(projekt.id, geschoss, gewerk)
      .then((x: any) => setD(x))
      .catch((e: unknown) =>
        setFehler(e instanceof Error ? e.message : String(e)))
      .finally(() => setLaeuft(false));
  };

  useEffect(laden, [api, projekt, geschoss, gewerk]);

  // --- Die Ausdehnung des Rasters -------------------------------------
  const rahmen = useMemo(() => {
    const z = d?.zellen as any[] | undefined;

    if (!z || z.length === 0) return null;

    return {
      x0: Math.min(...z.map((c) => c.x)),
      x1: Math.max(...z.map((c) => c.x)),
      y0: Math.min(...z.map((c) => c.y)),
      y1: Math.max(...z.map((c) => c.y)),
    };
  }, [d]);

  // ═══════════════════════════════════════════════════════════════════════
  // DIE FARBE IST DIE FREIE HÖHE, NICHT „FREI ODER NICHT"
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Ein Ja/Nein verschenkt die Auskunft: Zwischen 160 mm und 900 mm liegt
  // die Entscheidung, ob ein Kanal dort durchgeht. Die Abstufung zeigt sie.
  const farbe = (frei: number, hatBand: boolean) => {
    if (!hatBand) return '#d4d4d0';
    if (frei <= 0) return '#8c2f33';
    if (frei < 150) return '#b5544a';
    if (frei < 300) return '#c98a4b';
    if (frei < 600) return '#9aa84f';
    return '#4e8a55';
  };

  useEffect(() => {
    const c = leinwand.current;

    if (!c || !d || !rahmen) return;

    const zellen = d.zellen as any[];

    const breite = rahmen.x1 - rahmen.x0 + 1;
    const hoehe = rahmen.y1 - rahmen.y0 + 1;

    // Eine Zelle so gross, dass 4.857 davon noch auf einen Bildschirm
    // passen und man einzelne noch trifft.
    const s = Math.max(3, Math.min(14,
      Math.floor(900 / Math.max(breite, hoehe))));

    c.width = breite * s;
    c.height = hoehe * s;

    const g = c.getContext('2d');

    if (!g) return;

    g.clearRect(0, 0, c.width, c.height);

    for (const z of zellen) {
      g.fillStyle = farbe(z.freiMm ?? 0, z.freiVonMm != null);

      // Y wird gespiegelt: Im Modell wächst es nach Norden, auf dem
      // Bildschirm nach unten. Ohne die Spiegelung stünde der Plan auf dem
      // Kopf, und niemand fände sich zurecht.
      g.fillRect((z.x - rahmen.x0) * s,
                 (rahmen.y1 - z.y) * s, s - (s > 5 ? 1 : 0),
                 s - (s > 5 ? 1 : 0));
    }

    // ═════════════════════════════════════════════════════════════════════
    // WAS RESERVIERT IST, WIRD SCHRAFFIERT UND NICHT NUR EINGEFÄRBT
    // ═════════════════════════════════════════════════════════════════════
    //
    // Eine Reservierung ist eine Absicht und kein Bauteil. Die gleiche Farbe
    // wie eine Belegung würde behaupten, dort liege schon etwas — und die
    // Absicht lässt sich verhandeln, der gezeichnete Kanal nicht.
    g.strokeStyle = '#1f2937';
    g.lineWidth = 1;

    for (const z of zellen) {
      if (!(z.reserviert?.length > 0)) continue;

      const px = (z.x - rahmen.x0) * s;
      const py = (rahmen.y1 - z.y) * s;

      g.beginPath();
      g.moveTo(px, py + s);
      g.lineTo(px + s, py);
      g.stroke();
    }

    // --- Die eigene Auswahl -------------------------------------------
    if (auswahl.size > 0 || (zieht && zieherZiel)) {
      const gezogen = new Set(auswahl);

      if (zieht && zieherZiel)
        for (let x = Math.min(zieht.x, zieherZiel.x);
                 x <= Math.max(zieht.x, zieherZiel.x); x++)
        for (let y = Math.min(zieht.y, zieherZiel.y);
                 y <= Math.max(zieht.y, zieherZiel.y); y++)
          gezogen.add(`${x}/${y}`);

      g.fillStyle = 'rgba(255,255,255,0.55)';
      g.strokeStyle = '#111827';
      g.lineWidth = 1;

      for (const k of gezogen) {
        const [x, y] = k.split('/').map(Number);

        const px = (x - rahmen.x0) * s;
        const py = (rahmen.y1 - y) * s;

        g.fillRect(px, py, s, s);
        g.strokeRect(px + 0.5, py + 0.5, s - 1, s - 1);
      }
    }
  }, [d, rahmen, auswahl, zieht, zieherZiel]);

  const zelleUnter = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const c = leinwand.current;

    if (!c || !d || !rahmen) return null;

    const r = c.getBoundingClientRect();
    const breite = rahmen.x1 - rahmen.x0 + 1;
    const s = c.width / breite;

    return {
      x: rahmen.x0
         + Math.floor((e.clientX - r.left) * (c.width / r.width) / s),
      y: rahmen.y1
         - Math.floor((e.clientY - r.top) * (c.height / r.height) / s),
    };
  };

  const beiBewegung = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const p = zelleUnter(e);

    if (!p) return;

    if (zieht) setZieherZiel(p);

    setUnterCursor((d.zellen as any[]).find(
      (z) => z.x === p.x && z.y === p.y) ?? null);
  };

  const beiDruck = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!absteckmodus) return;

    const p = zelleUnter(e);

    if (!p) return;

    setZieht(p);
    setZieherZiel(p);
  };

  const beiLoslassen = () => {
    if (!zieht || !zieherZiel) { setZieht(null); return; }

    const neu = new Set(auswahl);

    for (let x = Math.min(zieht.x, zieherZiel.x);
             x <= Math.max(zieht.x, zieherZiel.x); x++)
    for (let y = Math.min(zieht.y, zieherZiel.y);
             y <= Math.max(zieht.y, zieherZiel.y); y++)
      neu.add(`${x}/${y}`);

    setAuswahl(neu);
    setZieht(null);
    setZieherZiel(null);

    // ═════════════════════════════════════════════════════════════════════
    // DAS BAND WIRD VORGESCHLAGEN UND NICHT ABGEFRAGT
    // ═════════════════════════════════════════════════════════════════════
    //
    // Die Karte weiss, was in der Auswahl frei ist. Wer den Planer die Höhe
    // eintippen lässt, bekommt die Zahl aus dem Kopf — und die passt nicht zu
    // dem, was er gerade auf dem Schirm gesehen hat.
    //
    // Vorgeschlagen wird der ENGSTE Wert der Auswahl: Ein Korridor ist so
    // hoch, wie er an seiner niedrigsten Stelle durchgeht. Der Mittelwert
    // würde einen Kanal vorschlagen, der an drei Stellen ansteht.
    if (vonMm === '' && bisMm === '') {
      const getroffen = (d.zellen as any[]).filter(
        (z) => neu.has(`${z.x}/${z.y}`) && z.freiVonMm != null);

      if (getroffen.length > 0) {
        const unten = Math.max(...getroffen.map((z) => z.freiVonMm));
        const oben = Math.min(...getroffen.map((z) => z.freiBisMm));

        if (oben > unten) {
          setVonMm(String(Math.round(unten)));
          setBisMm(String(Math.round(oben)));
        }
      }
    }
  };

  const abstecken = async (trotzdem: boolean) => {
    if (!projekt) return;

    setSchickt(true);
    setMeldung(null);
    setBesetzt(null);

    try {
      const zellen = [...auswahl].map((k) => k.split('/').map(Number));

      const e = await api.reservierungAnlegen(projekt.id, {
        gewerk,
        planerName: planer,
        zweck,
        geschoss,
        hoeheVonMm: Number(vonMm),
        hoeheBisMm: Number(bisMm),
        zellen,
        fristTage: Number(frist) || undefined,
        trotzdem,
      });

      if (e.besetzt) {
        setBesetzt(e.daten?.ueberschneidungen ?? []);
        setMeldung((e.daten?.hinweise ?? [])[0] ?? null);
        return;
      }

      setMeldung((e.daten?.hinweise ?? []).join(' '));
      setAuswahl(new Set());
      setZweck('');
      setVonMm('');
      setBisMm('');
      laden();
    } catch (x) {
      setMeldung(x instanceof Error ? x.message : String(x));
    } finally {
      setSchickt(false);
    }
  };

  const kopf = (
    <PageHeader
      titel="Belegungsplan"
      beschreibung={'Wo im Deckenhohlraum noch Platz ist — VOR dem Zeichnen und nicht '
        + 'danach. Gerechnet je Rasterspalte: das Band von der Abhangdecke bis zur '
        + 'Decke darüber, minus die belegten Bänder der anderen Gewerke, minus den '
        + 'Montagezuschlag.'}
      meta={`${geschoss} · ${gewerk}`}
      aktionen={projekt && (
        <Link to={`/projekte/${projekt.id}/koordination`} className="m-knopf" style={{ textDecoration: 'none' }}>
          Koordination
        </Link>
      )}
    />
  );

  if (!projekt) {
    return (
      <>
        {kopf}
        <LoadingState text="Projekt wird geladen …" zeilen={5} />
      </>
    );
  }



  return (
    <>
      {kopf}

      <ContentSection titel="Auswahl" nackt>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                      alignItems: 'center' }}>
          <span className="schwach" style={{ fontSize: 12 }}>Geschoss</span>

          {['G00', 'G01', 'G02', 'G03', 'G04', 'G05'].map((g) => (
            <button key={g}
                    onClick={() => setGeschoss(g)}
                    aria-pressed={geschoss === g}
                    className={geschoss === g ? 'm-knopf m-knopf-haupt' : 'm-knopf'}>
              {g}
            </button>
          ))}

          <span className="schwach"
                style={{ fontSize: 12, marginLeft: 12 }}>für</span>

          {[['rlt', 'Lüftung'], ['hzg', 'Heizung'], ['klt', 'Kälte'],
            ['san', 'Sanitär'], ['spr', 'Sprinkler'], ['elt', 'Elektro']]
            .map(([k, t]) => (
            <button key={k}
                    onClick={() => setGewerk(k)}
                    aria-pressed={gewerk === k}
                    className={gewerk === k ? 'm-knopf m-knopf-haupt' : 'm-knopf'}>
              {t}
            </button>
          ))}
        </div>
      </ContentSection>

      {/* ═══════════════════════════════════════════════════════════════
           KORRIDOR ABSTECKEN
           ═══════════════════════════════════════════════════════════════ */}
      <div className="karte">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <button onClick={() => {
                    setAbsteckmodus(!absteckmodus);
                    setAuswahl(new Set());
                    setBesetzt(null);
                    setMeldung(null);
                  }}
                  aria-pressed={absteckmodus}
                  className={absteckmodus ? 'm-knopf m-knopf-haupt' : 'm-knopf'}>
            {absteckmodus ? 'Abstecken beenden' : 'Korridor abstecken'}
          </button>

          <Link to={`/projekte/${projekt.id}/koordination/reservierungen`}
                className="m-knopf" style={{ textDecoration: 'none' }}>
            Reservierungen
          </Link>

          {absteckmodus && (
            <span className="schwach" style={{ fontSize: 12 }}>
              Mit der Maus über die Karte ziehen — mehrmals, für einen Korridor
              über Eck. {auswahl.size > 0
                ? `${auswahl.size} Zellen gewählt.`
                : 'Noch nichts gewählt.'}
            </span>
          )}

          {absteckmodus && auswahl.size > 0 && (
            <button onClick={() => setAuswahl(new Set())}
                    style={{ fontSize: 12 }}>
              Auswahl leeren
            </button>
          )}
        </div>

        {absteckmodus && auswahl.size > 0 && (
          <div className="m-feldgitter m-feldgitter-3" style={{ marginTop: 12 }}>
            <Feld
              beschriftung="Zweck"
              hinweis="Wofür wird der Platz gebraucht?"
              wert={zweck}
              beiAenderung={(w) => setZweck(w as string)}
              platzhalter="Hauptkanal Zuluft RLT-03"
              pflicht
            />
            <Feld
              beschriftung="Planer"
              wert={planer}
              beiAenderung={(w) => setPlaner(w as string)}
              platzhalter="Name"
              pflicht
            />
            <Feld
              art="zahl"
              beschriftung="Band von"
              einheit="mm"
              wert={vonMm === '' ? null : Number(vonMm)}
              beiAenderung={(w) => setVonMm(w === null ? '' : String(w))}
              pflicht
            />
            <Feld
              art="zahl"
              beschriftung="Band bis"
              einheit="mm"
              wert={bisMm === '' ? null : Number(bisMm)}
              beiAenderung={(w) => setBisMm(w === null ? '' : String(w))}
              pflicht
            />
            <Feld
              art="zahl"
              beschriftung="Frist"
              einheit="Tage"
              wert={frist === '' ? null : Number(frist)}
              beiAenderung={(w) => setFrist(w === null ? '' : String(w))}
              pflicht
            />
            <div className="m-feld m-feld-halb">
              <button className="m-knopf m-knopf-haupt"
                      disabled={schickt || !zweck || !planer || !vonMm || !bisMm}
                      onClick={() => abstecken(false)}>
                {schickt ? 'wird angelegt …' : 'Reservieren'}
              </button>
            </div>
          </div>
        )}

        {absteckmodus && auswahl.size > 0 && (
          <p className="schwach" style={{ fontSize: 12, marginTop: 10 }}>
            Das Band ist aus der Auswahl vorgeschlagen — der{' '}
            <strong>engste</strong> Wert darin, denn ein Korridor ist so hoch,
            wie er an seiner niedrigsten Stelle durchgeht. Die Frist ist
            Pflicht: Ohne sie reserviert jeder alles, und nach drei Monaten ist
            der Hohlraum voll mit Absichten.
          </p>
        )}

        {besetzt && (
          <div className="hinweis fehler" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Der Platz ist schon reserviert
            </div>

            <DataTable
              spalten={[
                {
                  schluessel: 'gewerk', titel: 'Gewerk', immerSichtbar: true,
                  wert: (u: any) => u.gewerk,
                },
                { schluessel: 'planer', titel: 'Planer', wert: (u: any) => u.planerName },
                { schluessel: 'zweck', titel: 'Zweck', wert: (u: any) => u.zweck },
                {
                  schluessel: 'hoehe', titel: 'Höhe', einheit: 'mm',
                  ausrichtung: 'rechts',
                  wert: (u: any) => u.hoeheVonMm,
                  inhalt: (u: any) =>
                    `${Math.round(u.hoeheVonMm)}–${Math.round(u.hoeheBisMm)}`,
                },
                {
                  schluessel: 'zellen', titel: 'Zellen', art: 'zahl',
                  wert: (u: any) => u.zellen,
                },
                {
                  schluessel: 'gueltig', titel: 'gültig bis', art: 'datum',
                  wert: (u: any) => u.gueltigBis,
                  inhalt: (u: any) =>
                    new Date(u.gueltigBis).toLocaleDateString('de-DE'),
                },
              ]}
              zeilen={besetzt as any[]}
              schluessel={(u: any) => u.id}
              suche={false}
              spaltenwahl={false}
              dicht
            />

            <div style={{ marginTop: 10, display: 'flex', gap: 10,
                          alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="m-knopf" onClick={() => abstecken(true)}
                      disabled={schickt}>
                Trotzdem reservieren
              </button>

              <span className="schwach" style={{ fontSize: 12 }}>
                Über 2 m Raster entscheidet nicht das Werkzeug: Wenn die Planer
                sich einig sind, geht es — und der andere bekommt eine Meldung.
              </span>
            </div>
          </div>
        )}

        {meldung && !besetzt && (
          <div className="hinweis" style={{ marginTop: 12 }}>{meldung}</div>
        )}
      </div>

      {fehler && (
        <ErrorState
          meldung="Der Belegungsplan konnte nicht gerechnet werden."
          technisch={fehler}
        />
      )}

      {laeuft && <LoadingState text="Belegung wird gerechnet …" zeilen={6} />}

      {d && !laeuft && (
        <>
          <KpiReihe>
            <KpiCard titel="Rasterspalten" wert={d.spalten} />
            <KpiCard titel="Mit nutzbarem Band" wert={d.mitBand} />
            <KpiCard
              titel="Haben noch Platz" wert={d.frei}
              zustand={d.frei > 0 ? 'gut' : undefined}
              zustandText={d.frei > 0 ? 'hier lässt sich planen' : undefined}
            />
            <KpiCard
              titel="Voll" wert={d.voll}
              zustand={d.voll > 0 ? 'warnung' : undefined}
              zustandText={d.voll > 0 ? 'kein Band mehr frei' : undefined}
            />
            <KpiCard titel="Frei im Mittel" wert={d.mittelFreiMm} einheit="mm" />
          </KpiReihe>

          <div className="karte">
            <div className="karte-titel">
              {geschoss} — freie Höhe je Rasterspalte
            </div>

            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap',
                          alignItems: 'flex-start' }}>
              <div style={{ overflow: 'auto', maxWidth: '100%' }}>
                <canvas ref={leinwand}
                        onMouseMove={beiBewegung}
                        onMouseDown={beiDruck}
                        onMouseUp={beiLoslassen}
                        onMouseLeave={() => {
                          setUnterCursor(null);
                          if (zieht) beiLoslassen();
                        }}
                        style={{ imageRendering: 'pixelated',
                                 cursor: 'crosshair',
                                 border: '1px solid var(--linie)' }} />
              </div>

              {/* --- Was unter dem Zeiger liegt ------------------------- */}
              <div style={{ minWidth: 230, fontSize: 13 }}>
                <div style={{ fontSize: 12, fontWeight: 600,
                              marginBottom: 8 }}>
                  {unterCursor
                    ? `Spalte ${unterCursor.x}/${unterCursor.y}`
                    : 'Zeiger über den Plan bewegen'}
                </div>

                {unterCursor && (
                  <div style={{ display: 'flex', flexDirection: 'column',
                                gap: 4 }}>
                    <div>
                      Band{' '}
                      {unterCursor.bandVonMm != null
                        ? `${Math.round(unterCursor.bandVonMm)} bis `
                          + `${Math.round(unterCursor.bandBisMm)}`
                        : <span style={{ color: 'var(--zustand-fehler)' }}>keines</span>}
                    </div>

                    {unterCursor.freiVonMm != null && (
                      <div style={{ fontWeight: 600 }}>
                        frei {Math.round(unterCursor.freiMm)} mm{' '}
                        <span className="schwach">
                          ({Math.round(unterCursor.freiVonMm)} bis{' '}
                          {Math.round(unterCursor.freiBisMm)})
                        </span>
                      </div>
                    )}

                    <div className="schwach" style={{ fontSize: 12 }}>
                      belegt von: {(unterCursor.gewerke ?? []).join(', ')
                                   || '—'}
                    </div>

                    {(unterCursor.reserviert ?? []).map((r: any, i: number) => (
                      <div key={i} style={{ fontSize: 12,
                                            borderLeft: '3px solid #1f2937',
                                            paddingLeft: 6, marginTop: 4 }}>
                        <strong>reserviert</strong> {r.gewerk} —{' '}
                        {r.zweck}
                        <br />
                        <span className="schwach">
                          {Math.round(r.vonMm)}–{Math.round(r.bisMm)} mm ·{' '}
                          {r.planerName}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* --- Die Legende ------------------------------------- */}
                <div style={{ marginTop: 18, display: 'flex',
                              flexDirection: 'column', gap: 5 }}>
                  {([['#4e8a55', 'über 600 mm'],
                     ['#9aa84f', '300 bis 600 mm'],
                     ['#c98a4b', '150 bis 300 mm'],
                     ['#b5544a', 'unter 150 mm'],
                     ['#8c2f33', 'nichts frei'],
                     ['#d4d4d0', 'kein Band']] as [string, string][])
                    .map(([f, t]) => (
                    <div key={t} style={{ display: 'flex', gap: 8,
                                          alignItems: 'center' }}>
                      <span style={{ width: 14, height: 14, background: f,
                                     display: 'inline-block' }} />
                      <span className="schwach"
                            style={{ fontSize: 12 }}>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="schwach" style={{ fontSize: 12, marginTop: 12 }}>
              Der <strong>Strich</strong> durch eine Zelle heißt: dort ist ein
              Korridor reserviert, aber noch nichts gezeichnet. Eine Absicht
              lässt sich verhandeln, ein gezeichneter Kanal nicht — deshalb
              trägt sie nicht dieselbe Farbe wie eine Belegung.
            </p>

            <p className="schwach" style={{ fontSize: 12, marginTop: 12 }}>
              Die Farbe ist die freie Höhe und nicht „frei oder nicht":
              Zwischen 160 mm und 900 mm liegt die Entscheidung, ob ein Kanal
              dort durchgeht. Grau heißt <strong>kein Band</strong> — dort
              fehlt die Abhangdecke, und das ist etwas anderes als „voll".
            </p>
          </div>

          {(d.hinweise ?? []).map((h: string, i: number) => (
            <div key={i} className="hinweis">{h}</div>
          ))}
        </>
      )}
    </>
  );
}
